import { apiFetch } from "@/service/api";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

type TemplateResponse = {
  template: Record<string, string>;
  types: Record<string, string>;
  meta: Record<
    string,
    { label: string; rows: string[]; columns: string[]; type: string }
  >;
};

export default function SurveyTemplate() {
  const { id } = useParams();
  const [data, setData] = useState<TemplateResponse | null>(null);

  useEffect(() => {
    const fetchSurveyTemplate = async () => {
      try {
        const response = await apiFetch(`survey/find-template/${id}`);
        const json = await response.json();
        setData(json);
      } catch (err) {
        console.error(err);
      }
    };
    if (id) fetchSurveyTemplate();
  }, [id]);

  if (!data) return <div className="p-6">Cargando plantilla...</div>;

  const { template, types = {}, meta } = data;

  const isImageType = (key: string) => {
    const t = types[key];
    return t === "file" || t === "signature" || t === "image";
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Datos de la plantilla Word</h1>
        <p className="text-sm text-gray-500 mt-1">
          Copia y pega estos tags tal cual en tu archivo .docx
        </p>
      </div>

      <section className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
        <div>
          <h2 className="font-semibold">Información general: plantilla y bucle</h2>
          <p className="mt-1 text-sm text-gray-700">
            Usa <code>{"{#respuestas}"}</code> al inicio del contenido que
            quieras repetir y <code>{"{/respuestas}"}</code> al final. Todo lo
            que esté entre esos tags se genera una vez por cada respuesta de la
            encuesta. Dentro del bloque puedes usar los campos de la respuesta,
            igual que los tags listados abajo.
          </p>
        </div>
        <pre className="overflow-x-auto rounded bg-white p-3 text-sm">
          <code>{`{#respuestas}
Nombre: {nombres}
Identificación: {numero_identificacion}
{/respuestas}`}</code>
        </pre>
        <p className="text-sm text-gray-700">
          También puedes descargar una plantilla Word de ejemplo para ver cómo
          organizar los tags:
        </p>
        <a
          href="/template/ej.docx"
          download="ejemplo-plantilla.docx"
          className="inline-flex h-9 items-center rounded border border-blue-300 bg-white px-3 text-sm font-medium text-blue-800 hover:bg-blue-100"
        >
          Descargar ejemplo Word
        </a>
      </section>

      <div className="grid gap-4">
        <h2 className="font-semibold text-lg">Campos simples</h2>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-3">Campo</th>
                <th className="p-3">Tag para Word</th>
                <th className="p-3">Tipo</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(template).map(([key, label]) => {
                if (meta[key]) return null;
                const type = types[key] || "text";
                const tag = isImageType(key) ? `{%${key}}` : `{${key}}`;
                return (
                  <tr key={key} className="border-t">
                    <td className="p-3 font-medium">{label}</td>
                    <td className="p-3">
                      <code className="bg-gray-100 px-2 py-1 rounded">
                        {tag}
                      </code>
                    </td>
                    <td className="p-3">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          isImageType(key)
                            ? "bg-purple-100 text-purple-700"
                            : "bg-gray-100"
                        }`}
                      >
                        {type}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {Object.keys(meta).length > 0 && (
        <div className="grid gap-4">
          <h2 className="font-semibold text-lg">Tablas / Matrices</h2>
          {Object.entries(meta).map(([qName, info]) => (
            <div
              key={qName}
              className="border rounded-lg p-4 space-y-3 bg-gray-50"
            >
              <div className="flex justify-between items-center">
                <b>
                  {info.label} - {`{${qName}}`}
                </b>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  {info.type} - {info.rows.length} filas x {info.columns.length}{" "}
                  cols
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Filas: {info.rows.join(", ")} | Columnas:{" "}
                {info.columns.join(", ")}
              </p>

              <div className="bg-white border rounded p-3 text-sm space-y-2">
                <div className="font-semibold">
                  En tu Word crea una tabla así:
                </div>
                <div className="border rounded overflow-hidden">
                  <div
                    className="bg-gray-100 p-2 font-bold flex gap-2"
                    style={{
                      display: "grid",
                      gridTemplateColumns: `150px repeat(${info.columns.length}, 1fr)`,
                    }}
                  >
                    <span>Fila</span>
                    {info.columns.map((c) => (
                      <span key={c}>{c}</span>
                    ))}
                  </div>
                  <div
                    className="p-2 flex gap-2 bg-yellow-50"
                    style={{
                      display: "grid",
                      gridTemplateColumns: `150px repeat(${info.columns.length}, 1fr)`,
                    }}
                  >
                    <span>
                      <code>
                        {`{#${qName}}`}
                        {`{row}`}
                        {`{/${qName}}`}
                      </code>
                    </span>
                    {info.columns.map((c) => (
                      <span key={c}>
                        <code>{`{${c.toLowerCase()}}`}</code>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-xs text-gray-500 bg-gray-900 text-white p-2 rounded">
                  Backend convierte <code>{qName}_0_0 = 100000</code> a{" "}
                  <code>{`{ row: "Remuneracion economica", ${info.columns[0].toLowerCase()}: "100000" }`}</code>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="border rounded-lg p-4 space-y-2 bg-purple-50 border-purple-200">
        <h3 className="font-semibold">Imágenes / Firmas / Archivos</h3>
        <p className="text-sm">
          Para estos usa <code>%</code> - Ej:
        </p>
        {Object.keys(types)
          .filter((k) => isImageType(k))
          .map((k) => (
            <code
              key={k}
              className="bg-gray-900 text-white px-3 py-2 rounded block mt-1"
            >
              {`{%${k}}`} - {template[k]}
            </code>
          ))}
        {Object.keys(types).filter((k) => isImageType(k)).length === 0 && (
          <p className="text-xs text-gray-500">
            No hay campos de tipo archivo/firma en esta encuesta
          </p>
        )}
      </div>
    </div>
  );
}
